/**
 * SeoPage — SEO & Keywords pro Firma: SEMrush-Daten (API-Abruf oder manueller
 * Import), KI-Bewertung mit Empfehlungen + Ads-Keyword-Vorschlaegen (1-Klick-
 * Uebernahme in Google Ads) und KI-Keyword-Ideen aus Branche + Website.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Search, Loader2, Download, ClipboardPaste, Sparkles, Lightbulb,
  TrendingUp, TrendingDown, Minus, Plus, CheckCircle2, AlertTriangle,
} from "lucide-react";
import PageHelp from "@/components/PageHelp";
import ApplyKeywordDialog from "@/components/seo/ApplyKeywordDialog";
import SeoHistory from "@/components/seo/SeoHistory";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PosTrend({ row }) {
  if (!row.prev_position || !row.position) return <Minus className="w-3 h-3 text-slate-300" />;
  if (row.position < row.prev_position) return <TrendingUp className="w-3 h-3 text-emerald-500" />;
  if (row.position > row.prev_position) return <TrendingDown className="w-3 h-3 text-red-500" />;
  return <Minus className="w-3 h-3 text-slate-300" />;
}

export default function SeoPage() {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [data, setData] = useState(null);
  const [keyConfigured, setKeyConfigured] = useState(false);
  const [busy, setBusy] = useState("");
  const [manualOpen, setManualOpen] = useState(false);
  const [manualText, setManualText] = useState("");
  const [ideas, setIdeas] = useState(null);
  const [ideaContext, setIdeaContext] = useState("");
  const [applyKw, setApplyKw] = useState(null);
  const [historyKey, setHistoryKey] = useState(0);

  useEffect(() => {
    axios.get(`${API}/companies`, { withCredentials: true })
      .then((r) => { setCompanies(r.data || []); if (r.data?.length) setCompanyId(r.data[0].id); })
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (!companyId) return;
    try {
      const r = await axios.get(`${API}/seo/${companyId}/data`, { withCredentials: true });
      setData(r.data.data);
      setKeyConfigured(r.data.semrush_key_configured);
    } catch (e) { toast.error(e.response?.data?.detail || "Laden fehlgeschlagen"); }
  }, [companyId]);

  useEffect(() => { setData(null); setIdeas(null); load(); }, [companyId, load]);

  const fetchApi = async () => {
    setBusy("fetch");
    try {
      const r = await axios.post(`${API}/seo/${companyId}/fetch`, { database: "ch", limit: 50 }, { withCredentials: true });
      toast.success(r.data.message);
      setHistoryKey((k) => k + 1);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "SEMrush-Abruf fehlgeschlagen"); }
    finally { setBusy(""); }
  };

  const importManual = async () => {
    setBusy("manual");
    try {
      const r = await axios.post(`${API}/seo/${companyId}/manual`, { text: manualText }, { withCredentials: true });
      toast.success(r.data.message);
      setManualOpen(false); setManualText("");
      setHistoryKey((k) => k + 1);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Import fehlgeschlagen"); }
    finally { setBusy(""); }
  };

  const evaluate = async () => {
    setBusy("eval");
    try {
      await axios.post(`${API}/seo/${companyId}/evaluate`, {}, { withCredentials: true });
      toast.success("KI-Bewertung erstellt");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "KI-Bewertung fehlgeschlagen"); }
    finally { setBusy(""); }
  };

  const genIdeas = async () => {
    setBusy("ideas");
    try {
      const r = await axios.post(`${API}/seo/${companyId}/keyword-ideas`,
        { context: ideaContext }, { withCredentials: true });
      setIdeas(r.data.ideas || []);
      toast.success(`${r.data.ideas?.length || 0} Keyword-Ideen erstellt`);
    } catch (e) { toast.error(e.response?.data?.detail || "Keyword-Ideen fehlgeschlagen"); }
    finally { setBusy(""); }
  };

  const ev = data?.ai_evaluation;
  const company = companies.find((c) => c.id === companyId);

  return (
    <div className="p-6 space-y-5" data-testid="seo-page">
      <PageHelp
        title="SEO & Keywords"
        text="SEMrush-Daten pro Firma: per API abrufen (Key unter Admin → API-Keys, Datenbank Schweiz) oder einen SEMrush-Export manuell einfügen. Die KI bewertet Rankings, gibt Empfehlungen und schlägt Keywords für Google Ads vor — jede Übernahme bestätigst du per Klick."
      />
      <div className="flex flex-wrap items-center gap-3">
        <Search className="w-6 h-6 text-[#2563EB]" />
        <h1 className="text-2xl font-bold">SEO &amp; Keywords</h1>
        <div className="flex-1" />
        <Select value={companyId} onValueChange={setCompanyId}>
          <SelectTrigger className="w-56" data-testid="seo-company-select"><SelectValue placeholder="Firma" /></SelectTrigger>
          <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {/* Datenquelle */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex flex-wrap items-center gap-2">
            SEMrush-Daten
            {data && (
              <>
                <Badge variant="outline" className="text-[10px]">{data.domain || "—"}</Badge>
                <Badge className={`text-[10px] ${data.source === "api" ? "bg-blue-100 text-blue-700 border-blue-200" : "bg-violet-100 text-violet-700 border-violet-200"}`}>
                  {data.source === "api" ? "via API" : "Manuell importiert"}
                </Badge>
                <span className="text-[11px] text-slate-400 font-normal">
                  Stand: {new Date(data.fetched_at).toLocaleString("de-CH")}
                </span>
              </>
            )}
            <div className="flex-1" />
            <Button size="sm" variant="outline" onClick={() => setManualOpen(true)} className="gap-1 h-8" data-testid="seo-manual-btn">
              <ClipboardPaste className="w-3.5 h-3.5" /> Manuell einfügen
            </Button>
            <Button size="sm" onClick={fetchApi} disabled={busy === "fetch" || !keyConfigured} className="gap-1 h-8" data-testid="seo-fetch-btn">
              {busy === "fetch" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
              Von SEMrush abrufen (CH)
            </Button>
          </CardTitle>
          {!keyConfigured && (
            <p className="text-xs text-amber-600 flex items-center gap-1" data-testid="seo-no-key-hint">
              <AlertTriangle className="w-3.5 h-3.5" /> Kein SEMrush API-Key hinterlegt (Admin → API-Keys) — manueller Import funktioniert trotzdem.
            </p>
          )}
          {company && !company.website && (
            <p className="text-xs text-amber-600">Keine Website im Firmenprofil hinterlegt — für den API-Abruf nötig.</p>
          )}
        </CardHeader>
        <CardContent>
          {data?.overview && (
            <div className="flex flex-wrap gap-4 mb-3 text-sm">
              <span><b>{data.overview.organic_keywords ?? "—"}</b> organische Keywords</span>
              <span><b>{data.overview.organic_traffic ?? "—"}</b> organischer Traffic/Monat</span>
              {data.overview.semrush_rank && <span>SEMrush-Rank <b>{data.overview.semrush_rank}</b></span>}
            </div>
          )}
          {!data && (
            <p className="text-sm text-slate-500" data-testid="seo-empty">
              Noch keine SEO-Daten für diese Firma. Von SEMrush abrufen oder einen Export manuell einfügen.
            </p>
          )}
          {data?.rows?.length > 0 && (
            <div className="max-h-96 overflow-y-auto border border-[var(--studio-border)] rounded-lg" data-testid="seo-keyword-table">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-[var(--studio-bg)]">
                  <tr className="text-left text-slate-500">
                    <th className="p-2">Keyword</th><th className="p-2">Pos.</th><th className="p-2"></th>
                    <th className="p-2">Volumen</th><th className="p-2">CPC</th><th className="p-2">Traffic %</th><th className="p-2">URL</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r, i) => (
                    <tr key={i} className="border-t border-[var(--studio-border)]">
                      <td className="p-2 font-medium">{r.keyword}</td>
                      <td className="p-2">{r.position ?? "—"}</td>
                      <td className="p-2"><PosTrend row={r} /></td>
                      <td className="p-2">{r.volume ?? "—"}</td>
                      <td className="p-2">{r.cpc ?? "—"}</td>
                      <td className="p-2">{r.traffic ?? "—"}</td>
                      <td className="p-2 max-w-[200px] truncate text-slate-400">{r.url}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Ranking-Verlauf */}
      <SeoHistory companyId={companyId} refreshKey={historyKey} isAdmin={user?.role === "admin"} />

      {/* KI-Bewertung */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#2563EB]" /> KI-Bewertung
            {data?.evaluated_at && (
              <span className="text-[11px] text-slate-400 font-normal">vom {new Date(data.evaluated_at).toLocaleString("de-CH")}</span>
            )}
            <div className="flex-1" />
            <Button size="sm" onClick={evaluate} disabled={busy === "eval" || !data?.rows?.length} className="gap-1 h-8" data-testid="seo-evaluate-btn">
              {busy === "eval" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              {ev ? "Neu bewerten" : "KI-Bewertung erstellen"}
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {!ev && <p className="text-sm text-slate-500">Die KI analysiert deine SEMrush-Daten: Stärken, Schwächen, Empfehlungen und Keyword-Vorschläge für Google Ads.</p>}
          {ev && (
            <>
              <p className="text-sm" data-testid="seo-eval-summary">{ev.summary}</p>
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-emerald-600">Stärken</p>
                  {ev.strengths.map((s, i) => (
                    <p key={i} className="text-xs flex gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />{s}</p>
                  ))}
                </div>
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-red-600">Schwächen</p>
                  {ev.weaknesses.map((s, i) => (
                    <p key={i} className="text-xs flex gap-1.5"><AlertTriangle className="w-3.5 h-3.5 text-red-500 shrink-0 mt-0.5" />{s}</p>
                  ))}
                </div>
              </div>
              <div className="space-y-1">
                <p className="text-xs font-semibold">Empfehlungen</p>
                {ev.recommendations.map((s, i) => (
                  <p key={i} className="text-xs flex gap-1.5"><Lightbulb className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />{s}</p>
                ))}
              </div>
              {ev.ads_keywords?.length > 0 && (
                <div className="space-y-1.5" data-testid="seo-eval-ads-keywords">
                  <p className="text-xs font-semibold">Keyword-Vorschläge für Google Ads</p>
                  {ev.ads_keywords.map((k, i) => (
                    <div key={i} className="flex items-center gap-2 border border-[var(--studio-border)] rounded-lg px-2.5 py-1.5 text-xs">
                      <span className="font-medium">{k.text}</span>
                      <Badge variant="outline" className="text-[9px]">{k.match_type}</Badge>
                      <span className="text-slate-400 flex-1 truncate">{k.reason}</span>
                      <Button size="sm" variant="outline" className="h-6 text-[10px] gap-1"
                        onClick={() => setApplyKw(k)} data-testid={`seo-apply-kw-${i}`}>
                        <Plus className="w-3 h-3" /> In Google Ads
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Keyword-Ideen */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Lightbulb className="w-4 h-4 text-amber-500" /> KI Keyword-Ideen
            <div className="flex-1" />
          </CardTitle>
          <p className="text-xs text-slate-500">Neue Suchbegriffe aus Branche + Website{data?.rows?.length ? " + SEMrush-Rankings" : ""} — direkt in Google-Ads-Anzeigengruppen übernehmbar.</p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Input value={ideaContext} onChange={(e) => setIdeaContext(e.target.value)}
              placeholder="Optionaler Fokus, z.B. 'Fokus auf Personal Training in Zürich'"
              data-testid="seo-ideas-context" />
            <Button onClick={genIdeas} disabled={busy === "ideas" || !companyId} className="gap-1 shrink-0" data-testid="seo-ideas-btn">
              {busy === "ideas" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lightbulb className="w-4 h-4" />}
              Ideen generieren
            </Button>
          </div>
          {ideas && (
            <div className="space-y-1.5" data-testid="seo-ideas-list">
              {ideas.map((k, i) => (
                <div key={i} className="flex items-center gap-2 border border-[var(--studio-border)] rounded-lg px-2.5 py-1.5 text-xs">
                  <span className="font-medium">{k.text}</span>
                  <Badge variant="outline" className="text-[9px]">{k.match_type}</Badge>
                  <span className="text-slate-400 flex-1 truncate">{k.reason}</span>
                  <Button size="sm" variant="outline" className="h-6 text-[10px] gap-1"
                    onClick={() => setApplyKw(k)} data-testid={`seo-idea-apply-${i}`}>
                    <Plus className="w-3 h-3" /> In Google Ads
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Manueller Import */}
      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>SEMrush-Export manuell einfügen</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">
            In SEMrush: Organische Recherche → Positionen → Export (CSV) — Inhalt hier einfügen.
            Erwartete Spalten: Keyword, Position, Search Volume, CPC, URL, Traffic (%). Semikolon-, Tab- oder Komma-getrennt.
          </p>
          <Textarea rows={10} value={manualText} onChange={(e) => setManualText(e.target.value)}
            placeholder={"Keyword;Position;Search Volume;CPC;Url;Traffic (%)\nfitnessstudio zuerich;12;1900;3.20;https://…;8.4"}
            className="font-mono text-xs" data-testid="seo-manual-textarea" />
          <Button onClick={importManual} disabled={busy === "manual" || !manualText.trim()} data-testid="seo-manual-submit">
            {busy === "manual" ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <ClipboardPaste className="w-4 h-4 mr-1" />}
            Importieren
          </Button>
        </DialogContent>
      </Dialog>

      <ApplyKeywordDialog open={!!applyKw} onClose={() => setApplyKw(null)} companyId={companyId} keyword={applyKw} />
    </div>
  );
}
